import { useEffect, useRef, useState } from 'react';

import Link from '@/components/utilities/SmartLink';

import { getDocPath } from '@/utils/docs';
import { useAppContext } from '@/components/utilities/AppContext';

// Palette pulled directly from lh-dev-support/pixel-integration-manual/index.html
// so the block matches the source design 1:1. brand-400 in the site theme is
// already #FF5D2A, but blue accents and off-orange (#ff7a4d) don't line up with
// the shared palette — kept as arbitrary values.
const ORANGE = '#FF5D2A';
const ORANGE_2 = '#ff7a4d';
const BLUE = '#1032CF';
const BLUE_2 = '#5877ff';

const LeadhubLogo = ({ className, colorPrimary = 'currentColor', colorAccent = ORANGE }) => (
  <svg className={className} viewBox="0 0 154 28" fill="none" xmlns="http://www.w3.org/2000/svg" aria-label="Leadhub">
    <path d="M32.6903 0.500244H29.7502V27.2018H32.6903V0.500244Z" fill={colorPrimary} />
    <path
      d="M52.6075 18.3977H38.4959C38.6429 21.8663 40.9583 24.8138 44.5957 24.8138C46.9111 24.8138 48.4902 23.508 49.2613 20.9336H52.3501C51.614 24.2542 48.968 27.5 44.4864 27.5C38.9369 27.5 35.6302 23.024 35.6302 17.6513C35.6303 11.7949 39.2309 7.80298 44.4119 7.80298C48.6751 7.80298 52.6799 10.7505 52.6799 17.0919C52.6799 17.5023 52.6433 17.95 52.6075 18.3977ZM38.6423 16.0099H49.7409C49.4826 12.4665 47.5726 10.3398 44.6326 10.3398C40.8482 10.3398 39.0841 12.5773 38.6431 16.0098"
      fill={colorPrimary}
    />
    <path
      d="M61.485 16.0471L63.396 15.9352C66.8874 15.7486 68.4292 15.4502 68.3932 13.9205C68.3565 11.9815 67.1447 10.3399 63.3215 10.3399C60.2355 10.3399 58.6183 11.8322 58.3243 13.7713H55.4578C56.0835 9.74284 59.3167 7.80298 63.616 7.80298C66.0406 7.80298 67.9517 8.39991 69.3115 9.63107C70.6713 10.8622 71.3318 13.0636 71.3318 16.3084V27.2015H68.3935V25.075C68.3997 24.3626 68.524 23.6563 68.761 22.9857C68.8319 22.8474 68.8697 22.694 68.8713 22.538C68.876 22.4795 68.8602 22.4211 68.8265 22.3733C68.7929 22.3255 68.7436 22.2913 68.6875 22.2769H68.614C68.4661 22.2769 68.3568 22.3888 68.32 22.6136C67.3275 25.6346 64.5354 27.5 61.0435 27.5C57.7369 27.5 54.8702 25.1123 54.8702 21.8663C54.8702 18.3977 57.0753 16.4579 61.4853 16.0471M63.2126 18.211L61.5588 18.4721C59.0964 18.8825 57.8101 19.7779 57.8101 21.6808C57.8101 23.5079 59.1331 25.0002 61.5955 25.0002C65.6004 25.0002 68.3568 22.0156 68.3935 16.5321C67.6218 17.4648 66.1518 17.7633 63.2126 18.211Z"
      fill={colorPrimary}
    />
    <path
      d="M92.3688 27.2032H89.4296V25.5712C89.4298 24.8984 89.5541 24.2316 89.7961 23.6054C89.9431 23.1974 89.9799 23.0118 89.7961 22.9017L89.6859 22.8636C89.5389 22.8636 89.4302 22.9749 89.3561 23.1974C88.473 25.868 86.0107 27.5002 83.0349 27.5002C78.2214 27.5002 74.7311 23.3458 74.7311 17.7089C74.7311 12.072 78.222 7.91733 83.0349 7.91733C86.0107 7.91733 88.5108 9.58646 89.3561 12.1458C89.4654 12.4425 89.5766 12.5537 89.7594 12.5156C89.8102 12.5053 89.8561 12.4777 89.8895 12.4375C89.9228 12.3972 89.9418 12.3467 89.9431 12.294C89.9009 12.1027 89.8393 11.9162 89.7594 11.7377C89.537 11.0813 89.4255 10.3914 89.4296 9.69725V0.5H92.3687L92.3688 27.2032ZM89.4296 17.7087C89.4296 13.8157 86.8937 10.5878 83.4759 10.5878C81.6384 10.5878 80.2061 11.2554 79.2138 12.6279C78.1837 13.9995 77.6692 15.6686 77.6692 17.7087C77.6692 19.7488 78.1837 21.418 79.2138 22.7893C80.2061 24.1617 81.6384 24.8294 83.4759 24.8294C86.8938 24.8294 89.4296 21.6024 89.4296 17.7084"
      fill={colorPrimary}
    />
    <path
      d="M112.23 15.9283V27.2019H109.29V16.0766C109.29 11.9226 107.6 10.439 104.697 10.439C101.757 10.439 98.7073 13.4062 98.7073 17.7457V27.2015H95.7672V0.5H98.7073V9.80949C98.7007 10.5055 98.5764 11.1952 98.3398 11.8485C98.2295 12.071 98.0448 12.5161 98.3398 12.5902H98.4133C98.4518 12.5957 98.4911 12.5932 98.5287 12.5831C98.5663 12.5729 98.6016 12.5552 98.6324 12.531C98.6632 12.5069 98.6889 12.4767 98.7081 12.4423C98.7273 12.4079 98.7395 12.37 98.744 12.3307C99.2218 11.218 100.03 10.2176 101.205 9.29043C102.344 8.36324 103.704 7.91814 105.247 7.91814C109.694 7.91814 112.23 10.4762 112.23 15.9283Z"
      fill={colorPrimary}
    />
    <path
      d="M115.486 19.4426V8.10229H118.426V19.2929C118.426 23.4716 120.117 24.9639 123.019 24.9639C125.959 24.9639 129.009 21.9793 129.009 17.6141V8.10229H131.949V27.2014H129.009V25.5972C128.999 24.8958 129.124 24.1992 129.376 23.5462C129.488 23.3224 129.671 22.8747 129.376 22.8H129.303C129.264 22.7946 129.225 22.7971 129.187 22.8073C129.149 22.8176 129.114 22.8354 129.083 22.8598C129.053 22.8841 129.027 22.9145 129.008 22.9491C128.989 22.9837 128.977 23.0218 128.973 23.0612C128.311 25.0376 125.665 27.4999 122.47 27.4999C118.023 27.4999 115.487 24.9266 115.487 19.4422"
      fill={colorPrimary}
    />
    <path
      d="M139.301 9.69725C139.305 10.391 139.193 11.0804 138.971 11.7362C138.897 11.9957 138.64 12.442 138.971 12.5148H139.044C139.191 12.5148 139.301 12.4035 139.374 12.1438C140.551 9.58548 142.792 7.91635 145.695 7.91635C150.51 7.91635 154 12.0707 154 17.7076C154 23.3445 150.51 27.4989 145.695 27.4989C142.865 27.4989 140.551 25.8297 139.374 23.1961C139.301 22.9736 139.191 22.8623 139.044 22.8623C139.014 22.8579 138.984 22.8605 138.955 22.8701C138.926 22.8797 138.9 22.896 138.879 22.9176C138.858 22.9392 138.841 22.9655 138.832 22.9946C138.822 23.0236 138.819 23.0545 138.824 23.0848C138.828 23.2633 138.865 23.4394 138.934 23.6039C139.176 24.2301 139.301 24.8969 139.301 25.5697V27.2017H136.361V0.5H139.301V9.69725ZM149.554 22.7886C150.546 21.4162 151.061 19.7482 151.061 17.7081C151.061 15.668 150.546 13.9989 149.554 12.6276C148.524 11.2551 147.092 10.5874 145.254 10.5874C141.836 10.5874 139.301 13.8144 139.301 17.7084C139.301 21.6024 141.836 24.8294 145.254 24.8294C147.092 24.8294 148.524 24.1617 149.554 22.7893"
      fill={colorPrimary}
    />
    <path d="M14.6541 15.2969H11.7139V27.2348H14.6541V15.2969Z" fill={colorAccent} />
    <path d="M14.653 0.533203H11.7128V12.4711H14.653V0.533203Z" fill={colorAccent} />
    <path d="M26.3656 12.3931H14.6062V15.3779H26.3656V12.3931Z" fill={colorAccent} />
    <path d="M11.7595 12.3931H0V15.3779H11.7595V12.3931Z" fill={colorAccent} />
  </svg>
);

const CopyButton = ({ text }) => {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  const handleClick = () => {
    if (!navigator?.clipboard) return;
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setCopied(false), 1600);
    });
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-label="Kopírovat"
      className={`absolute top-3 right-3 cursor-pointer rounded-full border px-3.5 py-1.5 text-xs transition-colors ${
        copied
          ? 'border-[#FF5D2A] bg-[#FF5D2A] text-white'
          : 'border-[#3a3a3a] bg-white/[0.06] text-[#d6d6d6] hover:border-[#FF5D2A] hover:bg-[#FF5D2A] hover:text-white'
      }`}
    >
      {copied ? 'Zkopírováno ✓' : 'Kopírovat'}
    </button>
  );
};

// Colour tokens for code snippets — one span per JS lexical class, matching
// the source's .t-str/.t-num/.t-bool/.t-key/.t-fn palette. Consumers hand-mark
// tokens with these helpers; the raw string prop is what gets copied.
const S = ({ children }) => <span className="text-[#a5d6a7]">{children}</span>;
const N = ({ children }) => <span className="text-[#ffab70]">{children}</span>;
const B = ({ children }) => <span className="text-[#ff7b72]">{children}</span>;
const K = ({ children }) => <span className="text-[#79c0ff]">{children}</span>;
const F = ({ children }) => <span className="font-semibold text-[#ff7a4d]">{children}</span>;

const CodeSnippet = ({ raw, children, className = '' }) => (
  <div className={`code relative ${className}`}>
    <CopyButton text={raw} />
    <pre className="m-0 overflow-auto rounded-2xl border border-[#232323] bg-[#0b0b0b] px-[22px] py-5 font-mono text-[13.5px] leading-[1.7] text-[#e9edf0]">
      <code>{children}</code>
    </pre>
  </div>
);

const Callout = ({ kind = 'warn', icon, title, children }) => {
  const styles =
    kind === 'info'
      ? 'border-[rgba(88,119,255,0.34)] bg-[rgba(16,50,207,0.12)]'
      : 'border-[rgba(255,93,42,0.34)] bg-[rgba(255,93,42,0.09)]';
  const iconBg = kind === 'info' ? 'bg-[#1032CF]' : 'bg-[#FF5D2A]';
  return (
    <div className={`flex items-start gap-4 rounded-2xl border p-5 ${styles}`}>
      <div
        className={`grid h-[30px] w-[30px] flex-none place-items-center rounded-lg text-base font-bold text-white ${iconBg}`}
      >
        {icon || '!'}
      </div>
      <div className="min-w-0 flex-1">
        {title && <h3 className="mb-1.5 text-[17px] font-medium text-white">{title}</h3>}
        <div className="m-0 break-words text-[#d9d9d9] [&_code]:rounded-[5px] [&_code]:bg-white/[0.08] [&_code]:px-1.5 [&_code]:py-px [&_code]:text-[13px] [&_code]:text-white">
          {children}
        </div>
      </div>
    </div>
  );
};

const Pill = ({ tone, children }) => {
  const styles = {
    req: 'border-[rgba(255,93,42,0.4)] bg-[rgba(255,93,42,0.16)] text-[#ff7a4d]',
    opt: 'border-[#3a3a3a] bg-white/[0.05] text-[#9a9a9a]',
    ess: 'border-[rgba(255,93,42,0.4)] bg-[rgba(255,93,42,0.16)] text-[#ff7a4d]'
  };
  return (
    <span
      className={`inline-block rounded-full border px-2.5 py-0.5 text-[11px] font-semibold whitespace-nowrap ${styles[tone] || styles.opt}`}
    >
      {children}
    </span>
  );
};

const ParamsTable = ({ rows }) => (
  <div className="-mx-4 overflow-x-auto px-4 sm:mx-0 sm:px-0">
    <table className="w-full min-w-[560px] border-collapse text-[13.5px]">
      <thead>
        <tr>
          {['Parametr', 'Typ', 'Povinnost', 'Popis'].map((h) => (
            <th
              key={h}
              className="border-b border-[#3a3a3a] px-2.5 py-2 text-left text-[10.5px] font-semibold tracking-[0.06em] text-[#9a9a9a] uppercase"
            >
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => {
          const isNested = row.nested;
          return (
            <tr key={i} className={`${isNested ? 'bg-white/[0.02]' : ''} border-b border-[#2a2a2a] last:border-b-0`}>
              <td
                className={`relative px-2.5 py-3 align-top ${isNested ? 'pl-[34px] before:absolute before:left-[14px] before:font-mono before:text-[13px] before:text-[#FF5D2A] before:opacity-90 before:content-["└"]' : ''}`}
              >
                <code className="font-mono text-[13px] font-semibold text-white">{row.name}</code>
                {row.example && <div className="mt-1 font-mono text-[11.5px] text-[#9a9a9a]">{row.example}</div>}
              </td>
              <td className="px-2.5 py-3 align-top">
                <code className="font-mono text-xs text-[#5877ff]">{row.type}</code>
              </td>
              <td className="px-2.5 py-3 align-top">
                <Pill tone={row.required ? 'req' : 'opt'}>{row.required ? 'povinné' : 'volitelné'}</Pill>
              </td>
              <td className="px-2.5 py-3 align-top text-[#d7d7d7]">{row.desc}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

const ParserBox = ({ items }) => (
  <div className="mt-6 rounded-xl border border-[rgba(88,119,255,0.28)] bg-[rgba(16,50,207,0.08)] px-4 py-4 sm:px-5">
    <div className="mb-2.5 flex items-center gap-2 text-[11px] font-semibold tracking-[0.08em] text-[#5877ff] uppercase">
      <span>⚙</span> Jak Leadhub vaše data zpracuje
    </div>
    <dl className="m-0 grid grid-cols-1 gap-x-0 gap-y-2 sm:[grid-template-columns:auto_1fr] sm:gap-x-[18px] sm:gap-y-[7px]">
      {items.map(([term, def]) => (
        <div key={term} className="contents">
          <dt className="text-[12.5px] font-semibold text-[#9a9a9a] sm:whitespace-nowrap">{term}</dt>
          <dd className="m-0 text-[13.5px] break-words text-[#dcdcdc]">{def}</dd>
        </div>
      ))}
    </dl>
  </div>
);

const TechNotes = ({ notes }) => (
  <details className="mt-5 border-t border-[#2a2a2a] pt-3.5">
    <summary className="cursor-pointer text-[13px] font-semibold text-white">
      Technické poznámky ({notes.length})
    </summary>
    <ul className="mt-3 list-disc pl-5">
      {notes.map((n, i) => (
        <li key={i} className="my-1.5 text-[13px] break-words text-[#bcbcbc]">
          {n}
        </li>
      ))}
    </ul>
  </details>
);

const EventCard = ({
  id,
  category,
  categoryColor = ORANGE,
  call,
  title,
  badges,
  whenToCall,
  purpose,
  snippet,
  snippetRaw,
  params,
  paramsEmptyText,
  parser,
  notes
}) => (
  <article
    id={id}
    className="mb-6 scroll-mt-6 rounded-2xl border border-[#2a2a2a] bg-[#161616]"
    style={{ '--cat': categoryColor }}
  >
    <div className="min-w-0 px-4 py-5 sm:px-6 sm:py-6 md:px-8 md:py-7">
      {/* Top row: only the category pill + badges share horizontal space. The
          call signature and sub-title live below so they always get the card's
          full width and don't wrap unnecessarily on narrow screens. */}
      <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
        <span
          className="inline-block rounded-full border px-2.5 py-0.5 text-[11px] font-semibold tracking-[0.08em] uppercase"
          style={{
            color: categoryColor,
            borderColor: `color-mix(in srgb, ${categoryColor} 45%, transparent)`,
            background: `color-mix(in srgb, ${categoryColor} 14%, transparent)`
          }}
        >
          {category}
        </span>
        {badges && <div className="flex flex-wrap items-center gap-2">{badges}</div>}
      </div>
      <h3 className="m-0 font-light">
        <code className="font-mono text-xl font-medium break-words text-white">{call}</code>
      </h3>
      <div className="mt-1 mb-4 text-sm text-[#9a9a9a]">{title}</div>

      <div className="mb-4 rounded-r-xl bg-[#1c1917] px-4 py-3" style={{ borderLeft: `3px solid ${categoryColor}` }}>
        <span
          className="mb-1 block text-[11px] font-semibold tracking-[0.09em] uppercase"
          style={{ color: categoryColor }}
        >
          Kdy volat
        </span>
        <p className="m-0 text-[14.5px] text-[#e2e2e2]">{whenToCall}</p>
      </div>

      <p className="mb-6 text-[#cfcfcf]">{purpose}</p>

      <h4 className="mt-6 mb-3 text-[11px] font-semibold tracking-[0.09em] text-[#9a9a9a] uppercase">Ukázka volání</h4>
      <CodeSnippet raw={snippetRaw}>{snippet}</CodeSnippet>

      <h4 className="mt-6 mb-3 text-[11px] font-semibold tracking-[0.09em] text-[#9a9a9a] uppercase">Parametry</h4>
      {params && params.length > 0 ? (
        <ParamsTable rows={params} />
      ) : (
        <p className="text-[#9a9a9a]">{paramsEmptyText || 'Tato událost nemá žádné parametry.'}</p>
      )}

      {parser && <ParserBox items={parser} />}
      {notes && notes.length > 0 && <TechNotes notes={notes} />}
    </div>
  </article>
);

const TOC_ITEMS = [
  { id: 'ev-pageview', code: 'pageview', label: 'Zobrazení stránky (pageview)' },
  { id: 'ev-viewcontent', code: 'viewContent', label: 'Zobrazení produktu' },
  { id: 'ev-viewcategory', code: 'viewCategory', label: 'Zobrazení kategorie' },
  { id: 'ev-setcart', code: 'setCart', label: 'Nastavení stavu košíku' },
  { id: 'ev-purchase', code: 'purchase', label: 'Událost nákupu (dokončení objednávky)' },
  { id: 'ev-identify', code: 'identify', label: 'Identifikace zákazníka (identify)' },
  { id: 'ev-subscribe', code: 'subscribe', label: 'Přihlášení k odběru' },
  { id: 'ev-unsubscribe', code: 'unsubscribe', label: 'Odhlášení z odběru (konkrétní listy)' },
  { id: 'ev-addtag', code: 'addTag', label: 'Přidání štítku' }
];

const StickyToc = () => {
  const [activeId, setActiveId] = useState(TOC_ITEMS[0].id);

  useEffect(() => {
    const els = TOC_ITEMS.map((i) => document.getElementById(i.id)).filter(Boolean);
    if (els.length === 0) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting);
        if (visible.length === 0) return;
        visible.sort((a, b) => a.target.getBoundingClientRect().top - b.target.getBoundingClientRect().top);
        setActiveId(visible[0].target.id);
      },
      { rootMargin: '-20% 0px -70% 0px', threshold: 0 }
    );

    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <nav className="sticky top-6 -mt-14 hidden lg:block">
      <div className="rounded-[18px] border border-[#2a2a2a] bg-[#161616] p-5">
        <h2 className="mb-3.5 text-[11px] font-semibold tracking-[0.12em] text-[#9a9a9a] uppercase">Události</h2>
        <ol className="m-0 list-none p-0 [counter-reset:t]">
          {TOC_ITEMS.map((item) => {
            const isActive = activeId === item.id;
            return (
              <li key={item.id} className="my-0.5 [counter-increment:t]">
                <a
                  href={`#${item.id}`}
                  className={`grid grid-cols-[auto_1fr] items-baseline gap-x-2.5 gap-y-1 rounded-[11px] px-3 py-2.5 transition-colors ${
                    isActive ? 'bg-[#1c1917]' : 'hover:bg-[#1c1917]'
                  }`}
                >
                  <span className="font-mono text-[11px] font-semibold text-[#FF5D2A] before:content-[counter(t,decimal-leading-zero)]" />
                  <code className="col-start-2 font-mono text-[12.5px] font-semibold text-white">{item.code}</code>
                  <span className="col-start-2 text-[11px] leading-[1.4] text-[#9a9a9a]">{item.label}</span>
                </a>
              </li>
            );
          })}
        </ol>
      </div>
    </nav>
  );
};

const EVENTS = [
  {
    id: 'ev-pageview',
    category: 'Inicializace',
    call: "lhi('pageview', {…})",
    title: 'Zobrazení stránky (pageview)',
    whenToCall:
      "Volání lhi('pageview') se provede při každém načtení stránky. V jednostránkových aplikacích (SPA) se stránka znovu nenačítá, proto pageview volejte ručně při každé změně routy (přechodu na jinou URL), aby se návštěva nové stránky změřila.",
    purpose:
      'Odešle událost o zobrazení stránky a spustí měření - čas návštěvníka na webu, zdroj návštěvy (referrer), pohyb po webu a použité zařízení.',
    snippetRaw: "lhi('pageview');",
    snippet: (
      <>
        <F>lhi</F>(<S>&apos;pageview&apos;</S>);
      </>
    ),
    params: [],
    paramsEmptyText: 'Tato událost nemá žádné parametry.',
    parser: [
      [
        'Co čte',
        'Čte časové razítko načtení stránky (povinné), délku pobytu na stránce, URL stránky, referrer (odkud návštěvník přišel) a titulek stránky.'
      ],
      [
        'Jak normalizuje',
        'K události se automaticky doplní IP adresa (→ geolokace: země, region, město) a User-Agent (→ operační systém, prohlížeč, typ zařízení).'
      ]
    ],
    notes: [
      'Inicializační snippet definuje globální frontu (w[f].q). Události volané přes lhi(...) ještě před dokončeným načtením agent.js se zařadí do fronty a zpracují se hromadně po startu (Agent.processGlobals).',
      "Stejný pageview nelze z téže URL odeslat dvakrát - connector si pamatuje lastPageviewLocation a druhé volání ze shodné adresy tiše zahodí (u callbacku vrací reject 'pageview can not be tracked twice from same location.').",
      'Kromě systémových parametrů (t=pageview) se automaticky odesílá dt = document.title a dr = document.referrer. Do každého requestu collect přidá tid (tracking_id), cid (client id z cookie), dl (aktuální URL), eid (UUID eventu), hit (timestamp), pvts (čas startu pageview) a pvtd (ms uplynulé od startu pageview).',
      'pageview zároveň sbírá statistiky (počet pageview v session, čas na stránce/webu) a historii navštívených URL do localStorage - tato data slouží mj. pro cílení popupů.',
      'Client id (cid) se ukládá do cookie na nejvyšší doméně.'
    ]
  },
  {
    id: 'ev-viewcontent',
    category: 'Procházení',
    call: "lhi('viewContent', {…})",
    title: 'Zobrazení produktu',
    whenToCall:
      'Právě jednou při zobrazení detailu jednoho produktu (stránka detailu produktu). Nevolejte na výpisu kategorie s více produkty - pole products má obsahovat jen ten jeden právě prohlížený produkt.',
    purpose:
      'Oznamuje Leadhubu, že si návštěvník prohlíží detail konkrétního produktu. Slouží k rozpoznání zájmu o produkty (personalizace, doporučování) a zároveň měří engagement - jak dlouho návštěvník na detailu produktu setrval. Kromě identifikace produktu se tak sbírá i časová stopa prohlížení.',
    snippetRaw: `lhi('viewContent', {\n  products: [{\n    product_id: 'product_id_1'\n  }]\n});`,
    snippet: (
      <>
        <F>lhi</F>(<S>&apos;viewContent&apos;</S>, {'{'}
        {'\n  '}
        <K>products</K>: [{'{'}
        {'\n    '}
        <K>product_id</K>: <S>&apos;product_id_1&apos;</S>
        {'\n  }'}]{'\n});'}
      </>
    ),
    params: [
      {
        name: 'products',
        example: "[{ product_id: 'ABC-123' }]",
        type: 'array<object>',
        required: true,
        desc: 'Pole produktů, které si návštěvník prohlíží - na detailu produktu typicky právě jeden objekt. Pokud je pole prázdné (nebo chybí), událost se okamžitě zahodí a nic se neodešle.'
      },
      {
        name: 'product_id',
        example: "'ABC-123'",
        type: 'string',
        required: true,
        nested: true,
        desc: 'Klíč uvnitř každého objektu v poli products - identifikátor produktu (kód/SKU) shodný s tím ve feedu.'
      },
      {
        name: 'type',
        example: "'product'",
        type: 'string',
        required: false,
        desc: "Typ obsahu. Výchozí hodnota je 'product'. Hodnotu 'master_product' použijte jen tehdy, když jde o nadřazený produkt s variantami/options."
      },
      {
        name: 'score',
        example: '1',
        type: 'number',
        required: false,
        desc: "Volitelné dodatečné skóre zájmu (data['score'] || 0). Pokud je nenulové, agent ho ihned připočte ke skóre produktu a odešle samostatný viewContent s aktuálním engagement.time_s a engagement.score."
      }
    ],
    parser: [
      [
        'Co čte',
        'Čte pole products a z každého produktu zejména product_id, dále volitelně name, quantity, value, currency a blok engagement (score, time_s).'
      ],
      [
        'Jak normalizuje',
        'product_id se převede na text a spáruje s vaším produktovým katalogem/feedem. Cena se přepočítá na primární měnu vašeho účtu - uloží se jak původní hodnota a měna, tak přepočtená.'
      ],
      [
        'Když pole chybí / je nevalidní',
        'Pokud v události není ani jeden produkt (nebo chybí product_id), Leadhub událost zahodí.'
      ]
    ],
    notes: [
      'Prázdné products => early return, nic se neodešle (byť onboarding snippet povinné pole nedovoluje být prázdné, reálně by pak událost neudělala nic).',
      'Engagement (čas na produktu) se sbírá JEN pro jeden produkt. Když products.length > 1, agent odešle jeden viewContent, ale sběr engagementu přeskočí (pojistka proti volání se 100 produkty na kategorii - jinak by to udělalo 100 requestů).',
      'Engagement běží jako Fibonacciho sekvence časovačů a periodicky posílá další viewContent s narůstajícím engagement.time_s a engagement.score; objekty engagement a visit_timestamp_ms doplňuje agent sám, zákazník je neposílá.',
      'Sběr času se zastaví při změně window.location.pathname (kompatibilita se SPA - přechod na jiný produkt) a při neaktivitě uživatele se pauzuje do obnovení aktivity.',
      'product_id je case-sensitive a musí odpovídat ID ve feedu, jinak Leadhub produkt nespáruje.',
      'Na kategorii/výpisu produktů se místo viewContentu volá viewCategory - viewContent je výhradně pro detail jednoho produktu.'
    ]
  },
  {
    id: 'ev-viewcategory',
    category: 'Procházení',
    call: "lhi('viewCategory', {…})",
    title: 'Zobrazení kategorie',
    whenToCall:
      'Při zobrazení stránky kategorie / výpisu produktů (ne detailu jednoho produktu). Volá se jednou za načtení stránky kategorie.',
    purpose:
      'Oznamuje Leadhubu, že návštěvník otevřel výpis (stránku) kategorie produktů. Slouží k tomu, aby Leadhub rozpoznal, které kategorie zboží si uživatel prohlíží, a mohl podle toho personalizovat doporučení a segmentaci.',
    snippetRaw: `lhi('viewCategory', {\n  category: 'Žena > Kabáty > Zimní kabáty'\n});`,
    snippet: (
      <>
        <F>lhi</F>(<S>&apos;viewCategory&apos;</S>, {'{'}
        {'\n  '}
        <K>category</K>: <S>&apos;Žena &gt; Kabáty &gt; Zimní kabáty&apos;</S>
        {'\n});'}
      </>
    ),
    params: [
      {
        name: 'category',
        example: "'Žena > Kabáty > Zimní kabáty'",
        type: 'string',
        required: true,
        desc: "Povinný string s kategorií. Doporučený formát je celá cesta stromem, jejíž úrovně se oddělují ' > ' (mezera-šipka-mezera), např. 'Žena > Kabáty > Zimní kabáty'. Poslat lze i prostý název kategorie bez hierarchie."
      }
    ],
    parser: [
      [
        'Co čte',
        'Čte pole category - buď textovou cestu ve formátu „Žena > Kabáty > Zimní kabáty“, nebo pole úrovní ["Žena", "Kabáty", "Zimní kabáty"].'
      ],
      [
        'Jak normalizuje',
        'Textová cesta se rozdělí podle „>“ na jednotlivé úrovně kategorie a ořeže se přebytečná mezera.'
      ],
      [
        'Když pole chybí / je nevalidní',
        'Chybějící nebo nesprávně formátovaná kategorie (není text ani pole) → událost se zahodí.'
      ]
    ],
    notes: [
      'Na rozdíl od viewContent nemá vlastní send* metodu - jde přímo přes sendDataEvent, nesbírá se žádný engagement ani čas.',
      "Při manuální integraci posílejte celou hierarchickou cestu od kořene oddělenou ' > ', ne jen list kategorie.",
      'Na kategorii nevolejte viewContent pro každý vypsaný produkt - od toho je právě viewCategory.'
    ]
  },
  {
    id: 'ev-setcart',
    category: 'Košík',
    call: "lhi('setCart', {…})",
    title: 'Nastavení stavu košíku',
    whenToCall:
      'Při každé změně košíku (přidání, odebrání, změna množství) a na stránce košíku. Posílejte vždy kompletní aktuální obsah košíku (nikoli jen změnu).',
    purpose:
      'Uloží kompletní aktuální stav košíku (nikoli jednotlivou změnu). Slouží k ukládání obsahu košíku, doporučování produktů a posílání připomínek na opuštěný košík. Existují i události addToCart a removeFromCart se stejnou signaturou (pole products), které popisují jednotlivou změnu (delta) - preferovanou a spolehlivější implementací je však posílat při každé změně celý košík přes setCart. Pro vysypání košíku pošlete setCart s prázdným polem (products: []). Po dokončení objednávky Leadhub košík vyprázdní automaticky, takže po události purchase už prázdný setCart posílat nemusíte.',
    snippetRaw: `lhi('setCart', {\n  products: [{\n    product_id: 'product_id',\n    quantity: 1,\n    value: 200,\n    currency: 'CZK'\n  }]\n});`,
    snippet: (
      <>
        <F>lhi</F>(<S>&apos;setCart&apos;</S>, {'{'}
        {'\n  '}
        <K>products</K>: [{'{'}
        {'\n    '}
        <K>product_id</K>: <S>&apos;product_id&apos;</S>,{'\n    '}
        <K>quantity</K>: <N>1</N>,{'\n    '}
        <K>value</K>: <N>200</N>,{'\n    '}
        <K>currency</K>: <S>&apos;CZK&apos;</S>
        {'\n  }'}]{'\n});'}
      </>
    ),
    params: [
      {
        name: 'products',
        example: "[{ product_id: 'ABC123', quantity: 1 }]",
        type: 'array<object>',
        required: true,
        desc: 'Pole objektů představující KOMPLETNÍ aktuální obsah košíku. Může být i prázdné pole `[]` (znamená prázdný košík). Vždy přepisuje celý stav, ne delta. Označeno jako povinné (hl-important) v onboarding snippetu.'
      },
      {
        name: 'product_id',
        example: "'ABC123'",
        type: 'string',
        required: true,
        nested: true,
        desc: 'Povinný klíč každé položky v poli `products` (hl-important). Stringová hodnota, která se MUSÍ shodovat s id produktu ve vašem produktovém feedu (jinak se produkt nespáruje pro doporučování).'
      },
      {
        name: 'quantity',
        example: '2',
        type: 'number',
        required: false,
        nested: true,
        desc: 'Volitelné množství dané položky (integer nebo string).'
      },
      {
        name: 'value',
        example: '200',
        type: 'number',
        required: false,
        nested: true,
        desc: 'Volitelná hodnota položky - cena za kus (integer, float nebo string). Dává smysl jen ve dvojici s currency.'
      },
      {
        name: 'currency',
        example: "'CZK'",
        type: 'string',
        required: false,
        nested: true,
        desc: 'Volitelná měna hodnoty (string, ISO kód). Dává smysl jen společně s `value`.'
      }
    ],
    parser: [
      [
        'Co čte',
        'Čte pole products (moderní formát), případně starší items / content_ids. U každé položky product_id, volitelně quantity, value, currency.'
      ],
      [
        'Jak normalizuje',
        'Stejně jako u viewContent - product_id se páruje s katalogem/feedem a cena se přepočítá na primární měnu účtu.'
      ],
      [
        'Když pole chybí / je nevalidní',
        'Prázdný košík posílejte jako products: [] (prázdné pole je v pořádku). Nevalidní produkt bez product_id se ignoruje.'
      ]
    ],
    notes: [
      'setCart nastavuje CELÝ košík - každé volání přepíše předchozí stav. Pro jednotlivé změny existují AddToCart/RemoveFromCart (delta), ale setCart je páteřní událost, na které stojí doporučování a připomínky opuštěného košíku.',
      '`products` smí být prázdné pole `[]` - to je validní signál prázdného košíku (ekvivalent EmptyCart). Onboarding to explicitně povoluje.',
      '`products` a `product_id` jsou jediné povinné klíče (hl-important); `quantity`, `value`, `currency` jsou volitelné (hl-optional). Podle onboardingu smí být hodnoty i string (quantity: integer/string, value: integer/float/string).',
      'Data jdou do CRM 1:1 (sendDataEvent = JSON.stringify celého objektu), takže se nic nehashuje ani nepřepisuje - pošlete přesně tvar, který chcete uložit.',
      'setCart posílejte jen při skutečné změně košíku - nevolejte ho opakovaně se stejnými daty (např. při každém překreslení stránky).',
      'Na velikosti písmen v názvu události nezáleží (setCart i SetCart fungují stejně); v manuálu používáme jednotný tvar setCart.'
    ]
  },
  {
    id: 'ev-purchase',
    category: 'Nákup',
    call: "lhi('purchase', {…})",
    title: 'Událost nákupu (dokončení objednávky)',
    badges: (
      <>
        <Pill tone="ess">klíčová událost</Pill>
        <span className="text-xs text-[#9a9a9a]">
          alias: <code className="rounded-md bg-[#1c1917] px-1.5 py-0.5 font-mono text-[#ddd]">checkout</code>
        </span>
      </>
    ),
    whenToCall: 'Přesně jednou při dokončení objednávky - na děkovací (thank-you) stránce po odeslání objednávky.',
    purpose:
      'Zaznamenává dokončenou objednávku. Slouží ke shromažďování nákupní historie zákazníka (zakoupené produkty, datum poslední objednávky, útrata, počet objednávek), k vyhodnocení kampaní vedoucích k nákupu a k analytice retence (noví vs. vracející se zákazníci). Jedna z nejdůležitějších událostí pixelu.',
    snippetRaw: `lhi('purchase', {\n  email: 'john@example.com',\n  value: 333,\n  currency: 'CZK',\n  products: [{\n    product_id: 'product_id_1',\n    quantity: 1,\n    value: 222,\n    currency: 'CZK'\n  }],\n  order_id: 'order_id_1',\n  user_id: 'user_id_1',\n  subscribe: ['newsletters'],\n  first_name: 'John',\n  last_name: 'Example',\n  phone: '+420221000000',\n  address: {\n    street: 'Prvního pluku 347/12A',\n    city: 'Praha',\n    zip: '18600',\n    country_code: 'CZ'\n  }\n});`,
    snippet: (
      <>
        <F>lhi</F>(<S>&apos;purchase&apos;</S>, {'{'}
        {'\n  '}
        <K>email</K>: <S>&apos;john@example.com&apos;</S>,{'\n  '}
        <K>value</K>: <N>333</N>,{'\n  '}
        <K>currency</K>: <S>&apos;CZK&apos;</S>,{'\n  '}
        <K>products</K>: [{'{'}
        {'\n    '}
        <K>product_id</K>: <S>&apos;product_id_1&apos;</S>,{'\n    '}
        <K>quantity</K>: <N>1</N>,{'\n    '}
        <K>value</K>: <N>222</N>,{'\n    '}
        <K>currency</K>: <S>&apos;CZK&apos;</S>
        {'\n  }'}],{'\n  '}
        <K>order_id</K>: <S>&apos;order_id_1&apos;</S>,{'\n  '}
        <K>user_id</K>: <S>&apos;user_id_1&apos;</S>,{'\n  '}
        <K>subscribe</K>: [<S>&apos;newsletters&apos;</S>],{'\n  '}
        <K>first_name</K>: <S>&apos;John&apos;</S>,{'\n  '}
        <K>last_name</K>: <S>&apos;Example&apos;</S>,{'\n  '}
        <K>phone</K>: <S>&apos;+420221000000&apos;</S>,{'\n  '}
        <K>address</K>: {'{'}
        {'\n    '}
        <K>street</K>: <S>&apos;Prvního pluku 347/12A&apos;</S>,{'\n    '}
        <K>city</K>: <S>&apos;Praha&apos;</S>,{'\n    '}
        <K>zip</K>: <S>&apos;18600&apos;</S>,{'\n    '}
        <K>country_code</K>: <S>&apos;CZ&apos;</S>
        {'\n  }'}
        {'\n});'}
      </>
    ),
    params: [
      {
        name: 'email',
        example: "'john@example.com'",
        type: 'string',
        required: true,
        desc: 'E-mailová adresa zákazníka - slouží jako unikátní identifikátor pro spárování objednávky s profilem v CRM. V onboarding snippetu je označen jako povinný (hl-important).'
      },
      {
        name: 'value',
        example: '333',
        type: 'number',
        required: true,
        desc: 'Celková hodnota objednávky (hl-important). Přijímá integer, float i string.'
      },
      {
        name: 'currency',
        example: "'CZK'",
        type: 'string',
        required: true,
        desc: 'Kód měny objednávky (hl-important).'
      },
      {
        name: 'products',
        example: "[{ product_id: 'product_id_1', quantity: 1, value: 222, currency: 'CZK' }]",
        type: 'array<object>',
        required: true,
        desc: 'Pole zakoupených produktů (hl-important). Každý objekt musí mít product_id (string) shodující se s id ve vašem produktovém feedu; volitelně quantity (int/string), value (int/float/string) a currency (string) na úrovni produktu.'
      },
      {
        name: 'order_id',
        example: "'order_id_1'",
        type: 'string',
        required: true,
        desc: 'Vaše ID objednávky. Formálně se událost zpracuje i bez něj (Leadhub vygeneruje vlastní náhradní identifikátor), ale je velmi doporučené ho posílat kvůli deduplikaci objednávek - aby se stejná objednávka nezapočítala dvakrát, například při souběhu pixelu s importem objednávek. Přijímá integer i string.'
      },
      {
        name: 'user_id',
        example: "'user_id_1'",
        type: 'string',
        required: false,
        desc: 'Váš unikátní identifikátor uživatele (string). Jde o silný identifikátor - pokud si nejste jistí jeho jednoznačností, raději ho do objektu vůbec nevkládejte. Stejné user_id poslané u více různých osob způsobí, že Leadhub tyto profily v CRM spojí do jednoho.'
      },
      {
        name: 'subscribe',
        example: "['newsletters']",
        type: 'string[]',
        required: false,
        desc: 'Pole kódů zájmových listů, do kterých má být zákazník při objednávce přihlášen (hl-optional, např. zaškrtnutý souhlas s newsletterem). Kódy jsou CASE-SENSITIVE a musí přesně odpovídat poli code v accounts.interestLists.'
      },
      {
        name: 'first_name',
        example: "'John'",
        type: 'string',
        required: false,
        desc: 'Křestní jméno zákazníka (hl-optional).'
      },
      {
        name: 'last_name',
        example: "'Example'",
        type: 'string',
        required: false,
        desc: 'Příjmení zákazníka (hl-optional).'
      },
      {
        name: 'phone',
        example: "'+420221000000'",
        type: 'string',
        required: false,
        desc: 'Telefonní číslo zákazníka (hl-optional).'
      },
      {
        name: 'address',
        example: "{ street: 'Prvního pluku 347/12A', city: 'Praha', zip: '18600', country_code: 'CZ' }",
        type: 'object',
        required: false,
        desc: 'Objekt s doručovací/fakturační adresou (hl-optional). Podklíče (vše string): street (ulice), city (město), zip (PSČ), country_code (kód země).'
      }
    ],
    parser: [
      [
        'Co čte',
        'Čte identifikaci zákazníka (e-mail, telefon, jméno, adresa), pole products, celkovou value + currency, order_id a volitelně subscribe.'
      ],
      [
        'Jak normalizuje',
        'E-mail se převede na malá písmena a ověří (musí obsahovat @ a tečku, max. 200 znaků). Telefon se normalizuje do mezinárodního tvaru (jen číslice a +, „00“ → „+“, doplní se předvolba účtu). Hodnota objednávky se přepočítá na primární měnu účtu.'
      ],
      [
        'Když pole chybí / je nevalidní',
        'Bez identifikace (e-mail / telefon) nebo bez value a currency se objednávka zahodí. Nevalidní e-mail → zahození. Chybějící order_id → varování (doporučeno doplnit kvůli deduplikaci).'
      ]
    ],
    notes: [
      'Deduplikace objednávek: pokud neuvedete order_id, Leadhub si sám vytvoří náhradní identifikátor z otisku objednávky ({value, currency, products}) a po dobu 10 minut jej drží. Opětovné načtení děkovací stránky do 10 minut tak nevytvoří duplicitní objednávku. Přesto silně doporučujeme posílat vlastní order_id.',
      'Pokud produkt nemá product_id, ale má id, agent hodnotu id automaticky zkopíruje do product_id.',
      'E-mail se do Leadhub CRM posílá v otevřené podobě jako identifikátor (v JSON parametru data). Hashování (sha256) e-mailu probíhá jen v reklamních konektorech (Facebook, Google), ne v Leadhub konektoru.'
    ]
  },
  {
    id: 'ev-identify',
    category: 'Identita a odběry',
    call: "lhi('identify', {…})",
    title: 'Identifikace zákazníka (identify)',
    whenToCall:
      'Vhodné volat v okamžiku, kdy získáte e-mail (a případně jméno) návštěvníka - typicky při registraci, přihlášení do klientské sekce, nebo v kroku objednávky, kde zákazník zadává e-mail a jméno.',
    purpose:
      'Přiřadí aktuální prohlížeč (anonymní návštěvu) ke konkrétnímu profilu v databázi zákazníků Leadhubu na základě e-mailové adresy. Díky tomu se dosavadní i budoucí chování návštěvníka spojí s jeho kontaktem. V rámci události lze kontakt (pokud udělil souhlas) rovnou přihlásit do zájmového listu.',
    snippetRaw: `lhi('identify', {\n  email: 'john@example.com',\n  subscribe: ['newsletters'],\n  user_id: 123,\n  first_name: 'John',\n  last_name: 'Example',\n  phone: '+420221000000'\n});`,
    snippet: (
      <>
        <F>lhi</F>(<S>&apos;identify&apos;</S>, {'{'}
        {'\n  '}
        <K>email</K>: <S>&apos;john@example.com&apos;</S>,{'\n  '}
        <K>subscribe</K>: [<S>&apos;newsletters&apos;</S>],{'\n  '}
        <K>user_id</K>: <N>123</N>,{'\n  '}
        <K>first_name</K>: <S>&apos;John&apos;</S>,{'\n  '}
        <K>last_name</K>: <S>&apos;Example&apos;</S>,{'\n  '}
        <K>phone</K>: <S>&apos;+420221000000&apos;</S>
        {'\n});'}
      </>
    ),
    params: [
      {
        name: 'email',
        example: "'john@example.com'",
        type: 'string',
        required: true,
        desc: 'E-mailová adresa uživatele - slouží jako unikátní identifikátor kontaktu, na který se navážou data prohlížeče. V onboardingu zvýrazněno jako povinné (hl-important). Bez e-mailu událost nemá smysl (nemá se k čemu přiřadit).'
      },
      {
        name: 'subscribe',
        example: "['newsletters']",
        type: 'string[]',
        required: false,
        desc: "Pole KÓDŮ zájmových listů, do kterých se má kontakt přihlásit. Kódy jsou case-sensitive a musí přesně odpovídat poli code v accounts.interestLists. Onboarding u pole píše 'Povinné pole', ale zvýrazňuje ho jako volitelné (hl-optional) a kód žádnou validaci nedělá - technicky nepovinné."
      },
      {
        name: 'user_id',
        example: '123',
        type: 'string',
        required: false,
        desc: 'Váš unikátní identifikátor uživatele (string). Jde o silný identifikátor - pokud si nejste jistí jeho jednoznačností, raději ho do objektu vůbec nevkládejte. Stejné user_id poslané u více různých osob způsobí, že Leadhub tyto profily v CRM spojí do jednoho.'
      },
      { name: 'first_name', example: "'John'", type: 'string', required: false, desc: 'Křestní jméno uživatele.' },
      { name: 'last_name', example: "'Example'", type: 'string', required: false, desc: 'Příjmení uživatele.' },
      {
        name: 'phone',
        example: "'+420221000000'",
        type: 'string',
        required: false,
        desc: 'Telefonní číslo uživatele (string, ideálně v mezinárodním formátu).'
      }
    ],
    parser: [
      ['Co čte', 'Čte e-mail, telefon, jméno a příjmení, váš identifikátor uživatele, případně adresu a subscribe.'],
      [
        'Jak normalizuje',
        'E-mail a telefon se normalizují stejně jako u objednávky (e-mail → malá písmena a validace, telefon → mezinárodní tvar). Podle e-mailu Leadhub spáruje anonymní prohlížeč s existujícím profilem v CRM.'
      ],
      [
        'Když pole chybí / je nevalidní',
        'Nevalidní e-mail → zahození. Musí být uvedeno alespoň jedno identifikační pole.'
      ]
    ],
    notes: [
      'subscribe je pole kódů zájmových listů a je case-sensitive: nesoulad s interestLists.code kontakt tiše nepřihlásí (žádná chyba, žádný consentRequest). Viz opakovaný bug s velikostí písmen u code.',
      "identify je voláno i automaticky interně z connectoru: z URL parametru utm_lhm (posílá se jako identify {tmid}) a utm_lhms (identify {sms_message_id}). Oboje je deduplikováno přes localStorage (seznam 'seen'), aby se stejné id neposílalo opakovaně."
    ]
  },
  {
    id: 'ev-subscribe',
    category: 'Identita a odběry',
    call: "lhi('subscribe', {…})",
    title: 'Přihlášení k odběru',
    whenToCall:
      'Ve chvíli, kdy zákazník aktivně vyjádří souhlas s odběrem - odeslání přihlašovacího formuláře newsletteru nebo zaškrtnutí souhlasu při registraci či objednávce.',
    purpose:
      'Přihlásí zákazníka (podle e-mailu) do jednoho nebo více zájmových listů. Slouží ke správě souhlasů a odběrů - na jeho základě se kontakt objeví jako odebírající v daném listu a mohou se mu posílat kampaně.',
    snippetRaw: `lhi('subscribe', {\n  email: 'john@example.com',\n  interests: ['newsletters']\n});`,
    snippet: (
      <>
        <F>lhi</F>(<S>&apos;subscribe&apos;</S>, {'{'}
        {'\n  '}
        <K>email</K>: <S>&apos;john@example.com&apos;</S>,{'\n  '}
        <K>interests</K>: [<S>&apos;newsletters&apos;</S>]{'\n});'}
      </>
    ),
    params: [
      {
        name: 'email',
        example: "'john@example.com'",
        type: 'string',
        required: true,
        desc: 'E-mailová adresa přihlašovaného kontaktu (klíč email).'
      },
      {
        name: 'interests',
        example: "['newsletters']",
        type: 'string[]',
        required: true,
        desc: 'Pole kódů zájmových listů, do kterých se má kontakt přihlásit. Kódy jsou CASE-SENSITIVE a musí přesně odpovídat poli code v accounts.interestLists (nesoulad znamená, že se přihlášení tiše nepropíše).'
      }
    ],
    parser: [
      ['Co čte', 'Čte kontakt (e-mail nebo telefon) a pole interests s kódy zájmových listů.'],
      [
        'Jak normalizuje',
        'E-mail/telefon se normalizují. Kódy listů se párují 1:1 podle pole code v Leadhubu (rozlišují velikost písmen).'
      ],
      [
        'Když pole chybí / je nevalidní',
        'Chybí-li kontakt nebo je pole interests prázdné/chybějící, událost se zahodí. Nesouhlasí-li kód listu přesně, přihlášení se tiše nepropíše.'
      ]
    ],
    notes: [
      'Samostatná událost subscribe používá klíč interests (pole kódů), zatímco u událostí identify a purchase se stejný záměr vyjadřuje polem subscribe. Obojí jsou kódy interest listů a jsou case-sensitive.'
    ]
  },
  {
    id: 'ev-unsubscribe',
    category: 'Identita a odběry',
    call: "lhi('unsubscribe', {…})",
    title: 'Odhlášení z odběru (konkrétní listy)',
    whenToCall: 'Když zákazník odhlásí odběr konkrétního listu - např. odškrtnutí newsletteru ve svém profilu.',
    purpose:
      'Odhlásí zákazníka (podle e-mailu) z konkrétních zájmových listů uvedených v interests. Ostatní odběry zůstávají zachovány. Slouží ke správě souhlasů na úrovni jednotlivých listů.',
    snippetRaw: `lhi('unsubscribe', {\n  email: 'john@example.com',\n  interests: ['newsletters']\n});`,
    snippet: (
      <>
        <F>lhi</F>(<S>&apos;unsubscribe&apos;</S>, {'{'}
        {'\n  '}
        <K>email</K>: <S>&apos;john@example.com&apos;</S>,{'\n  '}
        <K>interests</K>: [<S>&apos;newsletters&apos;</S>]{'\n});'}
      </>
    ),
    params: [
      {
        name: 'email',
        example: "'john@example.com'",
        type: 'string',
        required: true,
        desc: 'E-mailová adresa odhlašovaného kontaktu (klíč email).'
      },
      {
        name: 'interests',
        example: "['newsletters']",
        type: 'string[]',
        required: true,
        desc: 'Pole kódů zájmových listů, ze kterých se má kontakt odhlásit. Kódy jsou CASE-SENSITIVE a musí odpovídat poli code v accounts.interestLists.'
      }
    ],
    parser: [
      ['Co čte', 'Čte kontakt (e-mail nebo telefon) a pole interests s kódy listů, ze kterých se má kontakt odhlásit.'],
      [
        'Jak normalizuje',
        'Stejná normalizace kontaktu i párování kódů listů jako u přihlášení. Odhlásí pouze uvedené listy; ostatní odběry zůstanou.'
      ],
      ['Když pole chybí / je nevalidní', 'Chybějící kontakt nebo prázdné interests → zahození.']
    ],
    notes: ['Odhlašuje jen z listů uvedených v interests - pro odhlášení ze všech odběrů použijte unsubscribeFromAll.']
  },
  {
    id: 'ev-addtag',
    category: 'Segmentace',
    call: "lhi('addTag', {…})",
    title: 'Přidání štítku',
    whenToCall:
      'Kdykoli chcete kontakt označit vlastností pro segmentaci (např. souhlas, jazyk, hodnocení recenze, zařazení do skupiny). Klíč type určuje druh štítku, další klíče nesou jeho hodnotu.',
    purpose:
      'Přiřadí kontaktu/návštěvníkovi vlastní štítek (tag) pro segmentaci - např. souhlas, jazyk, hodnocení recenze nebo zařazení do skupiny. Preferovaný (novější) zápis je dvojice name + data: name určuje druh štítku a data nesou jeho hodnoty. Podporován je i starší zápis přes klíč type (viz poznámky).',
    snippetRaw: `lhi('addTag', {\n  name: 'customer_type',\n  data: {\n    level: 'vip',\n    score: 95,\n    is_active: true,\n    signed_up: { datatype: 'date', value: '2026-07-01' }\n  }\n});`,
    snippet: (
      <>
        <F>lhi</F>(<S>&apos;addTag&apos;</S>, {'{'}
        {'\n  '}
        <K>name</K>: <S>&apos;customer_type&apos;</S>,{'\n  '}
        <K>data</K>: {'{'}
        {'\n    '}
        <K>level</K>: <S>&apos;vip&apos;</S>,{'\n    '}
        <K>score</K>: <N>95</N>,{'\n    '}
        <K>is_active</K>: <B>true</B>,{'\n    '}
        <K>signed_up</K>: {'{ '}
        <K>datatype</K>: <S>&apos;date&apos;</S>, <K>value</K>: <S>&apos;2026-07-01&apos;</S>
        {' }'}
        {'\n  }'}
        {'\n});'}
      </>
    ),
    params: [
      {
        name: 'name',
        type: 'string',
        required: true,
        desc: "Název štítku - druh vlastnosti, kterou kontaktu přiřazujete (např. 'customer_type', 'consent', 'language')."
      },
      {
        name: 'data',
        type: 'object',
        required: false,
        desc: "Hodnoty štítku jako dvojice klíč-hodnota. Text (string), číslo (number) a pravdivostní hodnotu (boolean) lze poslat přímo. Datum/čas ale NELZE poslat jako prostý string (uložil by se jako text) - pošlete ho jako typovaný objekt { datatype: 'date', value: '2026-07-01' } nebo { datatype: 'datetime', value: '2026-07-01T12:00:00' }; hodnota se očekává v ISO 8601 a interpretuje se jako UTC. Typovaný zápis { datatype, value } lze použít i pro ostatní typy ('str', 'int', 'float', 'bool')."
      }
    ],
    parser: [
      ['Co čte', 'Čte typ štítku (type / name) a volitelně další klíče s hodnotou štítku (data).'],
      [
        'Jak normalizuje',
        'Hodnoty štítku musí být text, číslo, pravdivostní hodnota nebo datum (ISO). U typovaných hodnot se provede převod podle uvedeného datového typu.'
      ],
      [
        'Když pole chybí / je nevalidní',
        'Chybějící typ štítku nebo nepodporovaný datový typ hodnoty → událost se zahodí.'
      ]
    ],
    notes: [
      "Datum/čas se posílá výhradně jako typovaný objekt { datatype: 'date'|'datetime', value: '<ISO 8601>' } - jinak se hodnota uloží jako text. Datum se interpretuje jako UTC."
    ]
  }
];

const GENERAL_NOTES = [
  'Pořadí: na stránce musí být jako první inicializační snippet - ten vytvoří globální funkci lhi() a frontu událostí. pageview je pak první odeslaná událost a měří čas na stránce (pvts/pvtd se počítají od jeho volání). Ostatní události volejte kdykoli po snippetu; na jejich vzájemném pořadí nezáleží, protože se do načtení agenta ukládají do fronty a zpracují se hromadně.',
  "Názvy událostí nezáleží na velikosti písmen - Leadhub si název interně převede na malá písmena, takže lhi('pageview') i lhi('Pageview') fungují stejně. V manuálu se používá jednotný tvar (viewContent, setCart, viewCategory …).",
  'SPA / pathname: agent sleduje window.location.pathname. U ViewContentu běží engagement tracking (měření zájmu o produkt), který se zastaví, jakmile se pathname změní (přechod na jiný produkt/stránku). V single-page aplikacích proto při navigaci volejte odpovídající události znovu (nový pageview / viewContent), samotný pixel je needetekuje automaticky.',
  'E-mail: v události identify a purchase se předává jako string v klíči email a slouží jako unikátní identifikátor kontaktu pro spárování prohlížeče s profilem v databázi Leadhubu. Ve weblayers se e-mail před odesláním ořízne (trim). Přenos probíhá přes HTTPS na www.lhinsights.com.',
  'subscribe je pole kódů zájmových listů (interest list codes), NE názvů - kódy jsou case-sensitive a musí přesně odpovídat kódu listu v Leadhubu, jinak se přihlášení tiše zahodí.',
  'product_id v produktech: kanonický klíč je product_id (snake_case). Pokud produkt nemá product_id, ale má id, Leadhub použije jako záložní hodnotu id. Hodnota product_id se musí shodovat s id produktu ve vašem produktovém feedu.',
  'Konzistence product_id napříč událostmi: stejný produkt musí mít stejné product_id ve viewContent, setCart i purchase (a shodné s feedem), jinak Leadhub nespáruje prohlížení, košík a nákup na tentýž produkt.',
  'value/currency: u purchase jsou value (nezáporná hodnota objednávky) a currency (kód měny) povinné páry na úrovni celé objednávky. U jednotlivého produktu lze volitelně uvést vlastní value a currency (cena za položku) plus quantity (množství).',
  'order_id: silně doporučený. Pokud u purchase chybí, Leadhub si vygeneruje náhradní identifikátor a 10 minut jej drží podle otisku objednávky ({value, currency, products}) - zabraňuje to duplicitnímu započtení téže objednávky při reloadu děkovací stránky.'
];

const INIT_SNIPPET_RAW = `<!-- Begin Leadhub Pixel Code -->
<script>
  (function(w,d,x,n,u,t,p,f,s,o){f='LHInsights';w[n]=w[f]=w[f]||function(n,d){
  (w[f].q=w[f].q||[]).push([n,d])};w[f].l=1*new Date();w[f].p=p;s=d.createElement(x);
  s.async=1;s.src=u+'?t='+t+(p?'&p='+p:'');o=d.getElementsByTagName(x)[0];o.parentNode.insertBefore(s,o)
  })(window,document,'script','lhi','//www.lhinsights.com/agent.js','#tracking_id#');

  lhi('pageview');
</script>
<!-- End Leadhub Pixel Code -->`;

const PixelManualBlock = ({ pdfFile }) => {
  const pdfHref = pdfFile ? getDocPath(pdfFile) : null;
  const { locale } = useAppContext();
  const homeHref = locale && locale !== 'cs' ? `/${locale}` : '/';

  return (
    <div
      className="pixel-manual bg-black font-sans text-[#f2f2f2]"
      style={{ '--pm-orange': ORANGE, '--pm-orange-2': ORANGE_2, '--pm-blue': BLUE, '--pm-blue-2': BLUE_2 }}
    >
      {/* HERO */}
      <header className="relative overflow-hidden bg-black px-4 pt-10 pb-24 sm:px-6">
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              'radial-gradient(900px 420px at 82% -8%, rgba(255,93,42,0.30) 0%, transparent 62%), radial-gradient(700px 380px at 8% 12%, rgba(16,50,207,0.22) 0%, transparent 60%)'
          }}
        />
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.35]"
          style={{
            backgroundImage: 'radial-gradient(rgba(255,93,42,0.30) 1.3px, transparent 1.5px)',
            backgroundSize: '26px 26px',
            maskImage: 'linear-gradient(180deg, transparent, #000 40%, transparent 92%)',
            WebkitMaskImage: 'linear-gradient(180deg, transparent, #000 40%, transparent 92%)'
          }}
        />
        <div className="relative z-10 mx-auto max-w-[1180px]">
          <div className="mb-16 flex items-center justify-between">
            <Link href={homeHref} aria-label="Leadhub — domů">
              <LeadhubLogo className="block h-[26px] w-auto text-white" />
            </Link>
            {pdfHref && (
              <a
                href={pdfHref}
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-full border border-[#3a3a3a] px-3.5 py-1.5 text-xs tracking-wide text-[#9a9a9a] hover:border-[#FF5D2A] hover:text-white"
              >
                Stáhnout PDF ↓
              </a>
            )}
          </div>

          <div className="mb-5 inline-flex items-center gap-2.5 text-[13px] font-semibold tracking-[0.10em] text-[#ff7a4d] uppercase">
            <span className="inline-block h-2 w-2 rounded-full bg-[#FF5D2A] shadow-[0_0_0_4px_rgba(255,93,42,0.2)]" />
            Leadhub Pixel
          </div>

          <h1 className="mb-6 text-[clamp(38px,6.5vw,74px)] leading-[1.02] font-light tracking-[-0.03em] text-white">
            Integrační manuál <b className="font-medium text-[#FF5D2A]">měřicího kódu</b>
          </h1>

          <p className="m-0 max-w-[760px] text-[clamp(16px,1.6vw,18.5px)] text-[#cfcfcf]">
            Leadhub pixel je JavaScriptový měřicí kód (agent.js), který vložíte do svého webu nebo e-shopu. Naslouchá
            tomu, co návštěvník právě dělá - které stránky a produkty si prohlíží, co má v košíku, kdy se identifikuje
            či nakoupí - a v reálném čase posílá tato data do Leadhubu. Díky tomu Leadhub buduje kompletní a živou
            databázi zákazníků, na jejímž základě lze segmentovat kontakty a spouštět automatizace podle chování
            jednotlivých uživatelů.
          </p>

          <div className="mt-8 flex flex-wrap gap-2.5">
            {['Verze dokumentu: 2. 7. 2026', '9 událostí', 'Zdroj: agent.js · lhinsights.com'].map((chip) => (
              <span
                key={chip}
                className="rounded-full border border-[#3a3a3a] bg-white/[0.04] px-3.5 py-2 text-[13px] text-[#d5d5d5]"
              >
                {chip}
              </span>
            ))}
          </div>
        </div>
      </header>

      {/* LAYOUT */}
      <div className="mx-auto grid max-w-[1180px] items-start gap-8 px-4 pb-24 sm:px-6 lg:grid-cols-[262px_1fr] lg:gap-11">
        <StickyToc />

        <main className="min-w-0 pt-5">
          {/* Začínáme */}
          <section className="mb-14">
            <h2 className="mb-2 text-xs font-semibold tracking-[0.14em] text-[#ff7a4d] uppercase">Začínáme</h2>
            <p className="mb-6 text-[30px] leading-tight font-light tracking-[-0.02em] text-white">
              Inicializační měřicí kód
            </p>
            <div className="rounded-2xl border border-[#2a2a2a] bg-[#161616] p-5 sm:p-6 md:p-7">
              <p className="text-[#d7d7d7]">
                Snippet vložte do hlavičky (&lt;head&gt;) KAŽDÉ stránky webu - jako první, ještě před jakýmkoli voláním
                lhi(...). Placeholder #tracking_id# nahraďte svým unikátním tracking_id z Leadhubu. Snippet vytvoří
                globální funkci lhi() a frontu událostí a asynchronně načte agent.js z //www.lhinsights.com/agent.js;
                události volané dříve, než se agent stáhne, se uloží do fronty a zpracují se po jeho načtení. Hned za
                snippetem se volá první událost lhi(&apos;pageview&apos;). Máte-li více jazykových mutací, každá mutace
                potřebuje svůj vlastní tracking_id, aby se data posílala do správného účtu v Leadhubu.
              </p>
              <div className="mt-4">
                <CodeSnippet raw={INIT_SNIPPET_RAW}>
                  {`<!-- Begin Leadhub Pixel Code -->\n<script>\n  (function(w,d,x,n,u,t,p,f,s,o){f=`}
                  <S>&apos;LHInsights&apos;</S>
                  {`;w[n]=w[f]=w[f]||function(n,d){\n  (w[f].q=w[f].q||[]).push([n,d])};w[f].l=`}
                  <N>1</N>
                  {`*new Date();w[f].p=p;s=d.createElement(x);\n  s.async=`}
                  <N>1</N>
                  {`;s.src=u+`}
                  <S>&apos;?t=&apos;</S>
                  {`+t+(p?`}
                  <S>&apos;&amp;p=&apos;</S>
                  {`+`}
                  <K>p</K>
                  {`:`}
                  <S>&apos;&apos;</S>
                  {`);o=d.getElementsByTagName(x)[`}
                  <N>0</N>
                  {`];o.parentNode.insertBefore(s,o)\n  })(window,document,`}
                  <S>&apos;script&apos;</S>
                  {`,`}
                  <S>&apos;lhi&apos;</S>
                  {`,`}
                  <S>&apos;//www.lhinsights.com/agent.js&apos;</S>
                  {`,`}
                  <S>&apos;#tracking_id#&apos;</S>
                  {`);\n\n  `}
                  <F>lhi</F>
                  {`(`}
                  <S>&apos;pageview&apos;</S>
                  {`);\n</script>\n<!-- End Leadhub Pixel Code -->`}
                </CodeSnippet>
              </div>
            </div>
          </section>

          {/* Legenda */}
          <section className="mb-14">
            <h2 className="mb-2 text-xs font-semibold tracking-[0.14em] text-[#ff7a4d] uppercase">Jak číst manuál</h2>
            <p className="mb-6 text-[30px] leading-tight font-light tracking-[-0.02em] text-white">Legenda parametrů</p>
            <div className="rounded-2xl border border-[#2a2a2a] bg-[#161616] p-5 sm:p-6 md:p-7">
              <ul className="m-0 list-none p-0">
                <li className="my-2.5 text-[#d7d7d7]">
                  <Pill tone="req">povinné</Pill> - parametr je nutný; bez něj se událost nezpracuje správně (Leadhub ji
                  může zahodit).
                </li>
                <li className="my-2.5 text-[#d7d7d7]">
                  <Pill tone="opt">volitelné</Pill> - parametr není nutný; data jen doplňuje a upřesňuje.
                </li>
              </ul>
            </div>
            <div className="mt-4">
              <Callout kind="warn" title="ID produktů musí odpovídat feedu">
                <p className="m-0">
                  Hodnota <code>product_id</code>, kterou posíláte v událostech <code>viewContent</code>,{' '}
                  <code>setCart</code> a <code>purchase</code>, se{' '}
                  <strong className="font-semibold text-white">
                    musí přesně shodovat s ID produktu ve vašem produktovém feedu
                  </strong>
                  . Stejný produkt musí mít stejné <code>product_id</code> napříč všemi událostmi i ve feedu - jinak
                  Leadhub nespáruje prohlížení, košík a nákup na tentýž produkt a nefunguje doporučování, obsah košíku
                  ani nákupní historie.
                </p>
              </Callout>
            </div>
          </section>

          {/* Události */}
          <section className="mb-14">
            <h2 className="mb-2 text-xs font-semibold tracking-[0.14em] text-[#ff7a4d] uppercase">
              Referenční přehled
            </h2>
            <p className="mb-6 text-[30px] leading-tight font-light tracking-[-0.02em] text-white">
              Události měřicího kódu
            </p>
            {EVENTS.map((ev) => (
              <EventCard key={ev.id} {...ev} badges={ev.badges || <Pill tone="ess">klíčová událost</Pill>} />
            ))}
          </section>

          {/* Dobré vědět */}
          <section className="mb-14">
            <h2 className="mb-2 text-xs font-semibold tracking-[0.14em] text-[#ff7a4d] uppercase">Dobré vědět</h2>
            <p className="mb-6 text-[30px] leading-tight font-light tracking-[-0.02em] text-white">
              Obecné poznámky k integraci
            </p>
            <div className="rounded-2xl border border-[#2a2a2a] bg-[#161616] p-5 sm:p-6 md:p-7">
              <ul className="m-0 list-disc pl-5">
                {GENERAL_NOTES.map((n, i) => (
                  <li key={i} className="my-2 break-words text-[#d7d7d7]">
                    {n}
                  </li>
                ))}
              </ul>
            </div>
          </section>
        </main>
      </div>

      <footer className="border-t border-[#2a2a2a] px-6 py-8 text-center text-[13px] text-[#9a9a9a]">
        <div className="mb-3 flex justify-center">
          <LeadhubLogo className="block h-5 w-auto text-white" />
        </div>
        Leadhub Pixel - integrační manuál · vygenerováno 2. 7. 2026
      </footer>
    </div>
  );
};

export default PixelManualBlock;
